import React from "react";
import {
  preparationProfiles,
  profileComponents,
} from "../../data/bufferStandards";
import ChartLegend from "./ChartLegend";

const chart = {
  width: 780,
  height: 380,
  left: 76,
  right: 58,
  top: 28,
  bottom: 92,
};

const plotWidth = chart.width - chart.left - chart.right;
const plotHeight = chart.height - chart.top - chart.bottom;

const pointFor = (value, index) => {
  const x =
    chart.left + (plotWidth / (profileComponents.length - 1)) * index;
  const y = chart.top + plotHeight - (plotHeight * value) / 100;
  return { x, y };
};

const linePathFor = (values) =>
  values
    .map((value, index) => {
      const point = pointFor(value, index);
      return `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`;
    })
    .join(" ");

const componentLabels = [
  {
    short: "Rucking",
    label: "Belastbaarheid / rucking",
  },
  {
    short: "Hardlopen",
    label: "Hardlopen",
  },
  {
    short: "Trekken",
    label: "Trekkracht bovenlichaam",
  },
  {
    short: "Duwen",
    label: "Duwkracht / herhalingsuithouding",
  },
  {
    short: "Benen",
    label: "Onderlichaam kracht",
  },
  {
    short: "Romp",
    label: "Romp / herhalingscapaciteit",
  },
  {
    short: "Zwemmen",
    label: "Zwemvaardigheid",
  },
  {
    short: "Mentaal",
    label: "Mentale tolerantie",
  },
];

const ProfileDetailCards = ({ compact = false }) => (
  <div
    className={`mt-5 grid min-w-0 gap-4 ${
      compact
        ? "grid-cols-1 sm:grid-cols-2"
        : "grid-cols-[repeat(auto-fit,minmax(220px,1fr))]"
    }`}
  >
    {componentLabels.map((component, index) => (
      <div
        key={profileComponents[index]}
        className="min-w-0 rounded-lg border border-mmBorder bg-black/20 p-4 md:p-5"
      >
        <div className="min-w-0">
          <p className="break-words font-body text-sm font-semibold leading-snug text-mmText md:text-base">
            {component.label}
          </p>
        </div>
        <p className="mt-3 max-w-full break-words text-sm leading-relaxed text-mmTextMuted">
          Voorbeeld {preparationProfiles[0].values[index]} / Richtprofiel{" "}
          {preparationProfiles[1].values[index]}
        </p>
      </div>
    ))}
  </div>
);

const PreparationProfileChart = ({ compact = false, framed = true }) => {
  const Wrapper = framed ? "div" : "section";
  const wrapperClassName = framed
    ? "mm-card w-full min-w-0 overflow-hidden p-4 md:p-6"
    : "w-full min-w-0";

  return (
    <Wrapper className={wrapperClassName}>
      <div className="mb-5">
        <h3 className="font-display uppercase tracking-widest text-mmText text-xl md:text-2xl">
          Voorbereidingsprofiel
        </h3>
        <p className="mt-2 text-sm text-mmTextMuted leading-relaxed max-w-2xl">
          Voorbereiding draait niet alleen om hardlopen, pull-ups, push-ups en
          sit-ups. Marsen, belasting dragen, rompcontrole, zwemvaardigheid en
          mentale tolerantie bepalen mede hoe goed je lichaam de opleiding en
          het werk daarna kan dragen.
        </p>
      </div>

      <ChartLegend
        items={preparationProfiles.map((profile) => ({
          label: profile.label,
          color: profile.color,
        }))}
      />

      <div className="mt-5 min-w-0 overflow-hidden rounded-xl border border-mmBorder bg-black/20 p-3 md:p-4">
        <svg
          viewBox={`0 0 ${chart.width} ${chart.height}`}
          className="h-auto w-full max-w-full"
          role="img"
          aria-labelledby="preparation-profile-title preparation-profile-desc"
          preserveAspectRatio="xMidYMid meet"
        >
        <title id="preparation-profile-title">Voorbereidingsprofiel</title>
        <desc id="preparation-profile-desc">
          Een fictief kandidaatprofiel wordt vergeleken met een Mission Movement
          richtprofiel op acht brede voorbereidingscomponenten.
        </desc>

        {[0, 50, 100].map((tick) => {
          const y = chart.top + plotHeight - (plotHeight * tick) / 100;
          return (
            <g key={tick}>
              <line
                x1={chart.left}
                x2={chart.width - chart.right}
                y1={y}
                y2={y}
                stroke="rgba(234,234,234,0.15)"
                strokeWidth="1"
              />
              <text
                x={chart.left - 12}
                y={y + 4}
                textAnchor="end"
                fill="rgba(234,234,234,0.68)"
                fontSize="12"
              >
                {tick}
              </text>
            </g>
          );
        })}

        <line
          x1={chart.left}
          x2={chart.left}
          y1={chart.top}
          y2={chart.top + plotHeight}
          stroke="rgba(234,234,234,0.22)"
          strokeWidth="1"
        />
        <line
          x1={chart.left}
          x2={chart.width - chart.right}
          y1={chart.top + plotHeight}
          y2={chart.top + plotHeight}
          stroke="rgba(234,234,234,0.22)"
          strokeWidth="1"
        />

        {preparationProfiles.map((profile) => (
          <g key={profile.id}>
            <path
              d={linePathFor(profile.values)}
              fill="none"
              stroke={profile.color}
              strokeWidth={profile.id === "target" ? "4" : "3"}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={profile.id === "target" ? "0.98" : "0.82"}
            />
            {profile.values.map((value, index) => {
              const point = pointFor(value, index);
              return (
                <circle
                  key={`${profile.id}-${profileComponents[index]}`}
                  cx={point.x}
                  cy={point.y}
                  r={profile.id === "target" ? "5" : "4"}
                  fill="#0b0b0b"
                  stroke={profile.color}
                  strokeWidth="3"
                />
              );
            })}
          </g>
        ))}

        {componentLabels.map((component, index) => {
          const point = pointFor(0, index);
          return (
            <text
              key={component.short}
              x={point.x}
              y={chart.top + plotHeight + 38}
              textAnchor="middle"
              fill="rgba(234,234,234,0.76)"
              fontSize="13"
            >
              {component.short}
            </text>
          );
        })}

        <text
          x={22}
          y={chart.top + plotHeight / 2}
          textAnchor="middle"
          fill="rgba(234,234,234,0.68)"
          fontSize="11"
          transform={`rotate(-90 22 ${chart.top + plotHeight / 2})`}
        >
          0 tot 100, educatieve schaal
        </text>
        </svg>
      </div>

      <ProfileDetailCards compact={compact} />

      <p className="mt-4 border-l-2 border-mmAccent pl-4 text-xs md:text-sm leading-relaxed text-mmTextMuted">
        Dit is een vereenvoudigde educatieve weergave. Het laat zien waarom een
        bredere en sterkere basis je uitgangspositie verbetert. Het is geen
        garantie op selectie.
      </p>
    </Wrapper>
  );
};

export default PreparationProfileChart;
